import { MarketingNavButton } from "../marketing/mobile";
import { ContactsNavButton } from "../marketing/ContactsShell";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
// Barrel Scout. `Users` dropped from this import when the Contacts slot below
// became ours: nothing in this file renders that icon any more (our button
// draws it), and noUnusedLocals is on.
import { Home, ListTodo, Plus, Settings } from "lucide-react";
import { useTranslate } from "ra-core";
import { Link, matchPath, useLocation, useMatch } from "react-router";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { useState } from "react";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else {
    currentPath = false;
  }

  // Check if the app is running as a PWA (standalone mode)
  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  // Check if it's iOS on the web
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 bg-secondary"
      style={{
        // Barrel Scout. THE BAR IS SIZED BY ITS BUTTONS, ON PURPOSE. That is
        // why there is no `height` here any more and no `h-14` on the class
        // above -- the two together are the whole change, and either alone is
        // a regression.
        //
        // What was here was `h-14` plus, inline,
        //   height: "calc(var(--spacing)) * 6" + (isPwa && isWebiOS ? " + 15px" : "")
        // with the `* 6` OUTSIDE the calc(). That declaration is invalid, and
        // it did NOT fall back to the class the way it looks like it should: a
        // declaration invalid at computed-value time is dropped AFTER it has
        // already won the cascade, and the property then takes its INITIAL
        // value, which for `height` is `auto`. So the bar has always been as
        // tall as its buttons and never the 56px that both the class and the
        // calc() name. Measured in marketing/mobileNav.test.tsx, before this
        // change and after it: navH = rowH = 61.3125 at 320px and at 390px.
        //
        // Which makes this a repair of the MECHANISM, not of the behavior.
        // Removing only the inline height would let `h-14` apply and put
        // 61.31px of buttons inside a 56px bar: the obvious fix is the
        // regression, which is why the class goes with it.
        //
        // `paddingBottom` stays exactly as it was. It is a separate, valid
        // declaration, and with border-box plus `height: auto` the bar grows
        // by precisely the 15px this branch wanted. That used to be luck --
        // the height line was written to add that 15px and never added
        // anything -- and is now the stated mechanism.
        //
        // Enforced rather than remembered: mobileNav.test.tsx asserts the nav
        // is exactly its row plus this padding, and separately that every slot
        // sits vertically inside the bar. Reintroduce a fixed height at any
        // size, as a class or inline, and it fails.
        //
        // iOS bug: even though viewport is set correctly, the bottom safe area inset is not accounted for
        // So we manually add some padding to avoid the navigation being too close to the home bar
        paddingBottom: isPwa && isWebiOS ? 15 : undefined,
      }}
    >
      <div className="flex justify-center">
        <>
          <NavigationButton
            href="/"
            Icon={Home}
            label={translate("ra.page.dashboard")}
            isActive={currentPath === "/"}
          />
          {/*
            Barrel Scout. Guarded by marketing/check-injections.mjs.

            REPLACES upstream's own
              <NavigationButton href="/contacts" Icon={Users}
                label={translate("resources.contacts.name", {smart_count: 2})}
                isActive={currentPath === "/contacts"} />
            with the same slot, the same icon, the same translated label and
            the same class string -- plus a menu of the seven Contacts screens.

            WHY THE SWAP RATHER THAN A LINE BESIDE IT. Six of those screens
            (Follow-ups, Lifecycle, Stages, Content, Sequences, Outbound) are
            reachable ONLY through marketing/ContactsShell.tsx's tab strip,
            which layout/Header.tsx renders and MobileLayout does not render at
            all -- so on a phone they were unreachable, not merely awkward.
            Adding a seventh slot for them is the bar c2c12ea4 measured and
            rejected at 35.4px against Apple's 44pt guidance, so the section
            gets ONE slot with a submenu exactly as Pipeline does below.

            AND WHY THE ACTIVE TEST IS NOT `currentPath` ANY MORE. The Content
            library's rows are resource records: opening one goes to
            /content_assets/5, which matchPath("/contacts/*") does not match,
            so this slot would go dark on a page that is plainly a contacts
            page. ContactsNavButton uses isContactsSection from
            marketing/paths.ts, which the desktop tab strip uses too.
          */}
          <ContactsNavButton />
          <CreateButton />
          {/* Barrel Scout. Guarded by marketing/check-injections.mjs. */}
          <MarketingNavButton />
          <NavigationButton
            href="/tasks"
            Icon={ListTodo}
            label={translate("resources.tasks.name", { smart_count: 2 })}
            isActive={currentPath === "/tasks"}
          />
          <SettingsButton />
        </>
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      // Barrel Scout. THE BAR MUST SIZE ITSELF, not hold seven fixed slots.
      //
      // `w-16 px-1` was a FIXED 68px slot -- w-16 is 4rem and index.css:146
      // sets the mobile root font-size to 17px, so the class name says 64 and
      // the browser draws 68 -- and the base Button adds `shrink-0`, so
      // nothing could give. Seven items came to 476px of content in a 320px
      // bar and `justify-center` split the 156px overflow across BOTH ends:
      // Dashboard sat at left = -78 and rendered as "oard", Settings ended at
      // right = 398 and rendered as "Set", and with no overflow scrolling
      // neither was reachable. That reached production.
      //
      // This has to be fixed HERE because NavigationButton is upstream's and
      // is not exported, and because our own buttons share this one flex row
      // -- fixing only ours would leave the rest as fixed 68px slots in it and
      // the bar would still overflow. marketing/mobile.tsx and
      // marketing/ContactsShell.tsx each carry a copy of this class string on
      // a button in this same row, and all three must keep matching.
      //
      // Every added class was mutation-checked one at a time against
      // marketing/mobileNav.test.tsx, and the two that changed nothing --
      // `shrink` and a plain `px-0` -- were deleted rather than left in
      // looking load-bearing. What survives, and why:
      //
      //   `has-[>svg]:px-0` cancels the base size's `has-[>svg]:px-3`
      //                     (ui/button.tsx:25). The Link holds an icon, so
      //                     that variant always matches, and tailwind-merge
      //                     scopes its groups BY MODIFIER -- a plain `px-0`
      //                     does not touch it, which is exactly why `px-0`
      //                     measured as dead. The first version of this fix
      //                     had every other class right and still overflowed
      //                     -- 381px in a 320px bar, all of it 25.5px of
      //                     padding per slot.
      //
      //   `basis-0`, `grow` are what make the row share out the width. They
      //                     also make `shrink` dead: with a zero basis there
      //                     is never a shrink to perform, so the base
      //                     `shrink-0` it was written to cancel cannot bite.
      //
      //   `max-w-16`        keeps upstream's compact centered cluster once
      //                     there is room. Below ~476px the row fills the
      //                     width; above it the slots stop at 68px and
      //                     `justify-center` centers them exactly as before.
      //
      //   NO `min-w-0`      DELIBERATELY, and this is the subtle one. The
      //                     default `min-width: auto` floors each slot at its
      //                     own min-content width, and the labels are
      //                     `whitespace-nowrap`, so flex can compress the bar
      //                     to fit the screen but can never compress a label
      //                     into its neighbor. Adding `min-w-0` swaps the
      //                     clipped bar for overlapping labels: a different
      //                     bug, not a fix. Measured with it: "Dashboard"
      //                     ends at 47.6 and "Contacts" starts at 41.1.
      //
      // AND THE ROW NOW HOLDS SIX, NOT SEVEN. Sizing it made a seventh item
      // fit, but only just: the narrowest slot was 35.4px against Apple's
      // 44pt touch guidance, with 22px of slack in the whole bar. This
      // fork's two entries (Pipeline and Docs) are now ONE with a submenu --
      // see MarketingNavButton in marketing/mobile.tsx -- which is why the
      // numbers below are not the ones the first fix measured. Nothing in
      // this class string changed for it; the row simply has one less to
      // share out.
      //
      // Measured, in marketing/mobileNav.test.tsx:
      //   320px  54.4 49.4 [68 FAB] 49.4 49.4 49.4 = 320, nothing clipped
      //          (34205c91 wrote 53.2 49.7 here; re-measured on the same
      //          machine when the Contacts slot became ours it comes out
      //          54.4 49.4, same total, and it comes out that way on HEAD
      //          WITHOUT that change too -- so the old line was stale, not
      //          moved. The test asserts the 44pt rule, not these numbers.)
      //          and every slot clears 44pt on both axes
      //   390px  every slot 64.4
      //   767px  every slot capped at 68, cluster centered
      //   before the collapse, at 320px:
      //          54.4 45.3 [68 FAB] 39.8 35.4 35.4 41.8
      "flex-col gap-1 h-auto py-2 has-[>svg]:px-0 rounded-md basis-0 grow max-w-16",
      isActive ? null : "text-muted-foreground",
    )}
  >
    <Link to={href}>
      <Icon className="size-6" />
      <span className="text-[0.6rem] font-medium">{label}</span>
    </Link>
  </Button>
);

const CreateButton = () => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);

  return (
    <>
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            size="icon"
            className="h-16 w-16 rounded-full -mt-3"
            aria-label={translate("ra.action.create")}
          >
            <Plus className="size-10" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setContactCreateOpen(true);
            }}
          >
            {translate("resources.contacts.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setNoteCreateOpen(true);
            }}
          >
            {translate("resources.notes.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setTaskCreateOpen(true);
            }}
          >
            {translate("resources.tasks.forcedCaseName")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

const SettingsButton = () => {
  const translate = useTranslate();
  const location = useLocation();
  const isActive = !!matchPath("/settings", location.pathname);

  return (
    <NavigationButton
      href="/settings"
      Icon={Settings}
      label={translate("crm.settings.title")}
      isActive={isActive}
    />
  );
};
